import { toBlob } from 'html-to-image';

export type CaptureElementImageOptions = {
  filename?: string;
  shareTitle?: string;
  shareText?: string;
};

export type CaptureElementImageResult = {
  blob: Blob;
  /** True when the image was already handed off via the mobile share sheet. */
  shared: boolean;
};

function isMobileLikeDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // iPadOS reports as MacIntel but has touch points.
  const isIpadOs = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return /iPhone|iPad|iPod|Android/i.test(ua) || isIpadOs;
}

function canShareFiles(file: File): boolean {
  return typeof navigator !== 'undefined'
    && typeof navigator.canShare === 'function'
    && navigator.canShare({ files: [file] });
}

/**
 * Capture a DOM node as PNG.
 * On mobile, prefers the system share sheet (Save Image → Photos).
 * On desktop, returns the blob so the UI can download / preview reliably.
 */
export async function captureElementImage(
  element: HTMLElement,
  options: CaptureElementImageOptions = {},
): Promise<CaptureElementImageResult> {
  const filename = options.filename ?? `deadpan-${Date.now()}.png`;

  const blob = await toBlob(element, {
    cacheBust: true,
    pixelRatio: Math.min(window.devicePixelRatio || 2, 3),
    backgroundColor: '#ffffff',
    // Soft blur/transparency often paints poorly in DOM capture.
    style: {
      backdropFilter: 'none',
    },
  });

  if (!blob) {
    throw new Error('Failed to capture image');
  }

  const file = new File([blob], filename, { type: 'image/png' });

  // Share sheet is useful on phones. On Mac desktop it often opens Share
  // and never downloads — skip it there.
  if (
    isMobileLikeDevice()
    && canShareFiles(file)
    && typeof navigator.share === 'function'
  ) {
    try {
      await navigator.share({
        files: [file],
        title: options.shareTitle,
        text: options.shareText,
      });
      return { blob, shared: true };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return { blob, shared: true };
      }
      // Fall through so desktop-style download can still proceed.
    }
  }

  return { blob, shared: false };
}

export function downloadImageBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Delay revoke so Safari can start the download.
  window.setTimeout(() => URL.revokeObjectURL(url), 2_000);
}
